import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { tr } from "@/i18n";
import { format } from "date-fns";
import { Camera, Check, FileUp, Loader2, MapPin, Phone, TriangleAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fr_customer_token";
const TOKEN_ALT = "fr_customer_token";

const tok = () => localStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_ALT) || "";
const formatDetail = (d) => {
  if (d == null) return "Errore di rete, riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
};

/* Upload: allega file O scatta foto (Fase 3.4) — riutilizzabile */
export const UploadPair = ({ label, onUploaded, testid }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/account/incident-files`, fd, {
        headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "multipart/form-data" },
      });
      onUploaded(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <span className="text-[10px] font-bold text-gray-500 w-40 shrink-0">{label}</span>
      <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={upload} className="hidden" data-testid={`${testid}-file`} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={upload} className="hidden" data-testid={`${testid}-camera`} />
      <button type="button" onClick={() => camRef.current?.click()} className="h-8 rounded-lg bg-fr-navy text-white text-[10px] font-bold px-2.5 flex items-center gap-1" data-testid={`${testid}-btn-camera`}>
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Camera size={11} />} Scatta una foto
      </button>
      <button type="button" onClick={() => fileRef.current?.click()} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[10px] font-bold px-2.5 flex items-center gap-1" data-testid={`${testid}-btn-file`}>
        <FileUp size={11} /> Allega file
      </button>
    </div>
  );
};

/* ---------- SOCCORSO STRADALE ---------- */
export const SoccorsoCard = ({ activeTarga }) => {
  const [phone, setPhone] = useState("");
  useEffect(() => {
    axios.get(`${API}/settings/public`).then(({ data }) => setPhone(data.roadside_phone || "")).catch(() => {});
  }, []);
  return (
    <div className="rounded-2xl bg-fr-navy text-white p-4" data-testid="account-roadside">
      <div className="flex items-center gap-2">
        <TriangleAlert size={18} className="text-amber-300" />
        <p className="font-extrabold text-sm">{tr("Soccorso stradale")}</p>
      </div>
      <p className="text-[11px] text-white/70 mt-1">
        {activeTarga ? `Veicolo in uso: targa ${activeTarga} — comunica la targa all'operatore.` : "Nessun veicolo in uso al momento."}
      </p>
      {phone ? (
        <a href={`tel:${phone}`} className="mt-3 w-full rounded-xl bg-fr-red text-white font-extrabold text-sm py-3 flex items-center justify-center gap-2 hover:bg-red-700 transition-colors" data-testid="account-roadside-call">
          <Phone size={16} /> Chiama il soccorso {phone}
        </a>
      ) : (
        <p className="mt-3 text-[11px] text-amber-300">Numero soccorso non configurato: [DA COMPLETARE: impostalo nel gestionale]</p>
      )}
      {/* COMANDO SITO 3.2: posizione inviata al gestionale */}
      <button
        onClick={() => {
          if (!navigator.geolocation) { toast.error("Posizione non disponibile su questo dispositivo"); return; }
          navigator.geolocation.getCurrentPosition(
            (pos) => axios.post(`${API}/account/sos`, { lat: pos.coords.latitude, lng: pos.coords.longitude, note: `targa ${activeTarga || "-"}` }, { headers: { Authorization: `Bearer ${tok()}` } })
              .then(() => toast.success("Posizione inviata al gestionale: ti stiamo raggiungendo"))
              .catch(() => toast.error("Invio posizione fallito: chiama il numero")),
            () => toast.error("Permesso posizione negato: chiama il numero e comunica la tua posizione")
          );
        }}
        className="mt-2 w-full rounded-xl bg-white/10 border border-white/20 text-white font-bold text-xs py-2.5 hover:bg-white/20 transition-colors"
        data-testid="account-roadside-location"
      >
        Invia la mia posizione al gestionale
      </button>
      <p className="text-[9px] text-white/50 mt-2">Verificare che il numero corrisponda alla polizza RCA del veicolo.</p>
    </div>
  );
};

/* ---------- PROCEDURA INCIDENTE (wizard) ---------- */
const STEPS = [
  { key: "circolazione", label: "Carta di circolazione", required: true },
  { key: "identita", label: "Carta d'identità del conducente", required: true },
  { key: "patente", label: "Patente di guida", required: true },
  { key: "cai", label: "Modulo CAI / constatazione amichevole (se compilato)", required: false },
];

const DANZI_GUIDE = ["Fronte", "Retro", "Lato sinistro", "Lato destro", "Dettaglio del danno", "Contesto della strada"];

export const IncidentWizard = ({ onClose, onCreated, bookings }) => {
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [facts, setFacts] = useState("");
  const [cp, setCp] = useState({ counterpart_name: "", counterpart_plate: "", counterpart_insurer: "", counterpart_phone: "" });
  const [bookingRef, setBookingRef] = useState(bookings[0]?.reference || "");
  const [gps, setGps] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }),
        () => setGps(null),
        { timeout: 8000 }
      );
    }
  }, []);

  const addFile = (f) => {
    setFiles((fs) => [...fs, f]);
    toast.success("File caricato");
  };

  const danniCount = files.filter((f) => f.step === "danni").length;
  const missingReq = STEPS.filter((s) => s.required && !files.some((f) => f.step === s.key));

  const submit = async () => {
    if (missingReq.length) {
      toast.error(`Mancano: ${missingReq.map((s) => s.label).join(", ")}`);
      return;
    }
    if (danniCount < 4) {
      toast.error(`Servono almeno 4 foto dei danni/luogo (${danniCount}/4)`);
      return;
    }
    if (facts.trim().length < 10) {
      toast.error("Scrivi la tua versione dei fatti (min 10 caratteri)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/account/incidents`, {
        booking_reference: bookingRef, facts, ...cp, gps_lat: gps?.lat, gps_lng: gps?.lng, files,
      }, { headers: { Authorization: `Bearer ${tok()}` } });
      // COMANDO SITO 3.3: la segnalazione va anche al gestionale FABRI GROUP (coda se offline)
      axios.post(`${API}/account/incident-report`, {
        description: facts, place: gps ? `${gps.lat},${gps.lng}` : "", cai: { ...cp }, photos: files,
      }, { headers: { Authorization: `Bearer ${tok()}` } }).catch(() => {});
      toast.success(`Pratica ${data.reference} inviata: il gestionale è stato avvisato`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end md:items-center justify-center" onClick={onClose} data-testid="incident-wizard">
      <div className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-extrabold text-fr-navy flex items-center gap-2"><TriangleAlert size={16} className="text-fr-red" /> Ho avuto un incidente</p>
          <button onClick={onClose} className="text-xs font-bold text-gray-400" data-testid="incident-wizard-close">Chiudi</button>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">Posizione GPS {gps ? "catturata ✓" : "non autorizzata"} • data e ora automatiche • pratica inviata al gestionale con avviso immediato.</p>

        {step === 0 && (
          <div className="mt-4 space-y-3" data-testid="incident-step-0">
            <label className="block text-[10px] font-bold text-gray-400 uppercase">Veicolo / prenotazione
              <select value={bookingRef} onChange={(e) => setBookingRef(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="incident-booking-select">
                {bookings.length === 0 && <option value="">Nessuna prenotazione attiva</option>}
                {bookings.map((b) => <option key={b.reference} value={b.reference}>{b.reference} — {b.category} — targa {b.targa_assigned || "n/d"}</option>)}
              </select>
            </label>
            {STEPS.map((s) => (
              <UploadPair key={s.key} label={s.label} testid={`incident-up-${s.key}`} onUploaded={(d) => addFile({ step: s.key, path: d.path, filename: d.filename })} />
            ))}
            {files.filter((f) => f.step !== "danni").length > 0 && (
              <p className="text-[10px] text-gray-500" data-testid="incident-files-count">Caricati: {files.filter((f) => f.step !== "danni").map((f) => f.step).join(", ")}</p>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="mt-4 space-y-3" data-testid="incident-step-1">
            <p className="text-[11px] font-bold text-gray-500">Foto del luogo e dei danni — minimo 4 da angolazioni diverse:</p>
            {DANZI_GUIDE.map((g) => (
              <UploadPair key={g} label={g} testid={`incident-danni-${g.slice(0, 6).toLowerCase()}`} onUploaded={(d) => addFile({ step: "danni", path: d.path, filename: d.filename })} />
            ))}
            <p className={`text-[11px] font-bold ${danniCount >= 4 ? "text-green-700" : "text-amber-700"}`} data-testid="incident-danni-count">Foto danni caricate: {danniCount}/4 minimo</p>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mt-2">Dati della controparte (se presente)
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                <input value={cp.counterpart_name} onChange={(e) => setCp({ ...cp, counterpart_name: e.target.value })} placeholder="Nome e cognome" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="incident-cp-name" />
                <input value={cp.counterpart_plate} onChange={(e) => setCp({ ...cp, counterpart_plate: e.target.value })} placeholder="Targa" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm uppercase" data-testid="incident-cp-plate" />
                <input value={cp.counterpart_insurer} onChange={(e) => setCp({ ...cp, counterpart_insurer: e.target.value })} placeholder="Compagnia assicurativa" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="incident-cp-insurer" />
                <input value={cp.counterpart_phone} onChange={(e) => setCp({ ...cp, counterpart_phone: e.target.value })} placeholder="Telefono" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="incident-cp-phone" />
              </div>
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="mt-4" data-testid="incident-step-2">
            <label className="block text-[10px] font-bold text-gray-400 uppercase">La tua versione dei fatti
              <textarea value={facts} onChange={(e) => setFacts(e.target.value)} rows={5} placeholder="Descrivi com'è successo, dove, quando…" className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm" data-testid="incident-facts" />
            </label>
            <div className="mt-2 text-[11px] text-gray-500" data-testid="incident-recap">
              <p>Allegati: {files.length} ({files.filter((f) => f.step === "danni").length} danni)</p>
              {gps && <p className="flex items-center gap-1"><MapPin size={11} /> GPS: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</p>}
            </div>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          {step > 0 && <button onClick={() => setStep(step - 1)} className="flex-1 h-11 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm" data-testid="incident-back">Indietro</button>}
          {step < 2 ? (
            <button onClick={() => setStep(step + 1)} className="flex-1 h-11 rounded-xl bg-fr-navy text-white font-bold text-sm" data-testid="incident-next">Avanti</button>
          ) : (
            <button onClick={submit} disabled={busy} className="flex-1 h-11 rounded-xl bg-fr-red text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="incident-submit">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Invia la pratica
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- LISTA PRATICHE CLIENTE ---------- */
export const MyIncidents = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/account/incidents`, { headers: { Authorization: `Bearer ${tok()}` } }).then(({ data }) => setItems(data.incidents)).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid="account-incidents">
      <p className="text-sm font-extrabold text-fr-navy">Le tue pratiche sinistro</p>
      {items.length === 0 && <p className="text-xs text-gray-400 mt-2">Nessuna pratica aperta.</p>}
      {items.map((i) => (
        <div key={i.id} className="mt-2 rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs" data-testid={`account-incident-${i.reference}`}>
          <p className="font-bold text-fr-navy font-mono">{i.reference} <span className="text-[10px] font-sans font-bold text-amber-700">{i.status.toUpperCase()}</span></p>
          <p className="text-gray-500 mt-1">{i.facts.slice(0, 100)}…</p>
        </div>
      ))}
    </div>
  );
};

/* ---------- CONTRATTO (download firma) ---------- */
export const ContrattiCard = ({ bookings }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid="account-contracts">
      <p className="text-sm font-extrabold text-fr-navy">Contratti e verbali</p>
      {bookings.length === 0 && <p className="text-xs text-gray-400 mt-2">Nessuna prenotazione: i contratti appaiono qui dopo la conferma.</p>}
      {bookings.slice(0, 5).map((b) => (
        <div key={b.reference} className="mt-2 flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs" data-testid={`account-contract-${b.reference}`}>
          <span className="font-mono font-bold text-fr-navy">{b.reference}</span>
          <span className="text-gray-400">{b.category}</span>
          <a href={`${API}/contracts/${b.reference}/download`} onClick={async (e) => {
            e.preventDefault();
            try {
              const r = await axios.get(`${API}/contracts/${b.reference}/download`, { headers: { Authorization: `Bearer ${tok()}` }, responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url; a.download = `contratto_${b.reference}.pdf`; a.click();
              URL.revokeObjectURL(url);
            } catch { toast.error("Contratto non ancora disponibile: attendi la conferma del gestionale"); }
          }} className="ml-auto text-[10px] font-bold text-fr-navy underline" data-testid={`account-contract-download-${b.reference}`}>
            Scarica PDF
          </a>
        </div>
      ))}
      <p className="text-[9px] text-gray-400 mt-2">Contratto con firma OTP [DA VALIDARE CON LEGALE] — copia firmata sempre disponibile qui.</p>
    </div>
  );
};

/* ---------- C1 (ITERAZIONE 4): verifica email con OTP — obbligatoria prima della conferma ---------- */
export const VerifyEmailCard = ({ user, onVerified }) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const verified = user?.email_verified;

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/account/email/verify-request`, {}, { headers: { Authorization: `Bearer ${tok()}` } });
      setSent(true);
      toast[data.otp_ambiente_prova ? "info" : "success"](`Codice inviato a ${user.email}${data.otp_ambiente_prova ? ` (ambiente di prova: ${data.otp_ambiente_prova})` : ""}`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/account/email/verify`, { code }, { headers: { Authorization: `Bearer ${tok()}` } });
      toast.success("Email verificata: il gestionale può ora confermare le tue prenotazioni");
      onVerified?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (verified) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5 flex items-center gap-2" data-testid="account-email-verified">
        <Check size={16} className="text-green-600" />
        <p className="text-sm font-extrabold text-fr-navy">{tr("Email verificata")}</p>
        <span className="ml-auto text-[10px] text-gray-400">serve per confermare le prenotazioni</span>
      </div>
    );
  }
  return (
    <div className="bg-amber-50 rounded-2xl border border-amber-200 shadow-card p-5" data-testid="account-email-verify-card">
      <p className="font-extrabold text-fr-navy text-sm">{tr("Verifica la tua email")}</p>
      <p className="text-[11px] text-gray-600 mt-1">Prima della conferma della prenotazione serve un account con email verificata: riceverai un codice a 6 cifre.</p>
      {sent ? (
        <div className="mt-2 flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Codice a 6 cifre" inputMode="numeric" className="h-10 w-32 rounded-lg border border-amber-300 bg-white px-3 text-sm tracking-widest" data-testid="email-verify-code" />
          <button onClick={verify} disabled={busy || code.length !== 6} className="h-10 rounded-xl bg-fr-navy text-white text-xs font-bold px-4 disabled:opacity-60" data-testid="email-verify-submit">Verifica</button>
        </div>
      ) : (
        <button onClick={request} disabled={busy} className="mt-3 rounded-xl bg-fr-navy text-white text-xs font-bold px-4 py-2.5 disabled:opacity-60" data-testid="email-verify-request">
          {busy ? "Invio…" : "Invia il codice di verifica"}
        </button>
      )}
    </div>
  );
};

/* ---------- C5 (ITERAZIONE 4): proposte di modifica della prenotazione ---------- */
export const ProposalsCard = () => {
  const [items, setItems] = useState(null);
  const load = () => { axios.get(`${API}/account/proposals`, { headers: { Authorization: `Bearer ${tok()}` } }).then(({ data }) => setItems(data.proposals)).catch(() => setItems([])); };
  useEffect(() => { load(); }, []);

  const act = async (id, what) => {
    try {
      await axios.post(`${API}/proposals/${id}/${what}`, {}, { headers: { Authorization: `Bearer ${tok()}` } });
      toast.success(what === "accept" ? "Modifica applicata alla prenotazione" : "Proposta rifiutata: la prenotazione originale resta valida");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  if (!items || items.length === 0) return null;
  return (
    <section className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="account-proposals">
      <p className="font-extrabold text-fr-navy text-sm">{tr("Proposte di modifica dal gestionale")}</p>
      <p className="text-[11px] text-gray-500 mt-1">La tua prenotazione resta invariata finché non accetti una proposta.</p>
      {items.map((p) => (
        <div key={p.id} className="mt-2 rounded-xl bg-amber-50 border border-amber-100 p-3" data-testid={`proposal-${p.id}`}>
          <p className="text-xs text-gray-700">
            Nuove date: <b>{format(new Date(p.proposed.pickup_at), "d MMM HH:mm", { locale: "it" })}</b> → <b>{format(new Date(p.proposed.dropoff_at), "d MMM HH:mm", { locale: "it" })}</b>
            {p.proposed.targa && <> • targa {p.proposed.targa}</>}
            {p.proposed.total_price != null && <> • nuovo totale: <b className="text-fr-navy">€{p.proposed.total_price}</b></>}
          </p>
          {p.note && <p className="text-[11px] text-gray-500 mt-1">Note: {p.note}</p>}
          <p className="text-[10px] text-gray-400 mt-1">Scade: {format(new Date(p.expires_at), "d MMM HH:mm", { locale: "it" })} — dopo la scadenza la prenotazione originale resta valida</p>
          <div className="mt-2 flex gap-2">
            <button onClick={() => act(p.id, "accept")} className="rounded-lg bg-green-600 text-white text-[11px] font-bold px-3 py-1.5" data-testid={`proposal-accept-${p.id}`}>Accetta modifica</button>
            <button onClick={() => act(p.id, "reject")} className="rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 py-1.5" data-testid={`proposal-reject-${p.id}`}>Rifiuta</button>
          </div>
        </div>
      ))}
    </section>
  );
};

/* COMANDO SITO FASE 3: dati del cliente DAL GESTIONALE (via backend del sito) —
   prenotazioni e stato, avvisi di pagamento con link, contratti scaricabili, consensi privacy. */
export const GestionaleCard = () => {
  const [data, setData] = useState(null);
  const [consents, setConsents] = useState(null);
  const load = () => {
    axios.get(`${API}/account/gestionale/summary`, { headers: { Authorization: `Bearer ${tok()}` } }).then(({ data: d }) => setData(d)).catch(() => setData({ enabled: false }));
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${tok()}` } }).then(({ data: me }) => setConsents(me.consents || { marketing_offers: false, review: false, selfie: false })).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setConsent = (k, v) => {
    const next = { ...consents, [k]: v };
    setConsents(next);
    axios.post(`${API}/account/consents`, next, { headers: { Authorization: `Bearer ${tok()}` } }).then(() => toast.success("Preferenze salvate")).catch(() => toast.error("Errore salvataggio"));
  };
  const privacyReq = (kind) => {
    axios.post(`${API}/account/privacy/request`, { kind }, { headers: { Authorization: `Bearer ${tok()}` } }).then(({ data: d }) => toast.success(d.message)).catch(() => toast.error("Errore richiesta"));
  };
  if (!data || data.enabled === false) return null;
  return (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid="account-gestionale">
      <p className="font-extrabold text-fr-navy text-sm">Le tue noleggi (dal gestionale Fabri Group)</p>
      {data.offline && <p className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">{data.message || "Dati in aggiornamento, contattaci"}</p>}
      {(data.bookings || []).length === 0 && !data.offline && <p className="mt-2 text-[11px] text-gray-400">Nessun noleggio in corso.</p>}
      {(data.bookings || []).map((b) => (
        <div key={b.code} className="mt-2 rounded-xl bg-gray-50 border border-gray-100 p-3" data-testid={`gestionale-booking-${b.code}`}>
          <p className="text-xs font-bold text-fr-navy">{b.code} — {b.model || "auto assegnata dal gestionale"}</p>
          <p className="text-[11px] text-gray-600">{(b.pickup_at || "").slice(0, 16).replace("T", " ")} → {(b.dropoff_at || "").slice(0, 16).replace("T", " ")} • stato: {b.status}</p>
          {b.total_price != null && <p className="text-[11px] text-gray-600">Totale: <b>€{b.total_price}</b></p>}
          {b.payment_url && <a href={b.payment_url} target="_blank" rel="noreferrer" className="mt-1 inline-block rounded-lg bg-fr-navy text-white text-[11px] font-bold px-3 py-1.5" data-testid={`gestionale-pay-${b.code}`}>Completa il pagamento</a>}
          {b.contract_url && <a href={b.contract_url} target="_blank" rel="noreferrer" className="ml-2 mt-1 inline-block rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 py-1.5" data-testid={`gestionale-contract-${b.code}`}>Scarica il contratto firmato</a>}
        </div>
      ))}
      {consents && (
        <div className="mt-3 border-t border-gray-100 pt-3" data-testid="account-consents">
          <p className="text-[10px] font-bold text-gray-400 uppercase">Preferenze privacy (modificabili sempre)</p>
          {[
            ["marketing_offers", "Offerte promozionali"],
            ["review", "Richiesta di recensione dopo il noleggio"],
            ["selfie", "Foto/contenuti social"],
          ].map(([k, label]) => (
            <label key={k} className="mt-1 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={!!consents[k]} onChange={(e) => setConsent(k, e.target.checked)} className="accent-fr-navy" data-testid={`consent-${k}`} />
              {label}
            </label>
          ))}
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={() => privacyReq("accesso")} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3" data-testid="privacy-access-request">Richiedi copia dei tuoi dati</button>
            <button onClick={() => privacyReq("cancellazione")} className="h-8 rounded-lg bg-red-50 text-fr-red text-[11px] font-bold px-3" data-testid="privacy-delete-request">Richiedi cancellazione dati</button>
          </div>
        </div>
      )}
    </section>
  );
};
